import React from 'react';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import PlusIcon from '@mui/icons-material/Add';

import { allevaOrangeColor } from '../md/assets/jss/material-dashboard-pro-react.jsx';

const allevaOrange = allevaOrangeColor[0];

import { sortedDropdownItemLabels } from '../utils/utils';

import DA from '../store/ReduxAccess';

/* Call like this
    <SearchableContactHierarchy
      required={true}
      data={this.syntheticSearchableContactData}
      referrerType={this.state.referrerType}
      companyId={this.state.referrerOrgId}
      contactId={this.state.referralContactId}
      newOrgTypeFunc={() => alert('Should show Modal to define a new Org Type')}
      newCompanyFunc={() => alert('Should show Modal to define a new Referral Company')}
      newReferralContactFunc={() => alert('Should show Modal to define a new Referral Contact')}
      onOrgTypeSelect={(referrerType) => this.selectOrgType(referrerType)}
      onCompanySelect={(referrerType, referringOrgId) => this.selectCompany(referrerType, referringOrgId)}
      onContactSelect={(referrerType, referringOrgId, personId) => this.selectContact(referrerType, referringOrgId, personId)}
    />
 *
 * The data property should have the following structure:
 * {
 *   orgTypes: [
 *     {
 *       id: 'Cat:Hospital',
 *       name: 'Hospital',
 *     },
 *     ...
 *   ],
 *   businesses: [
 *     {
 *       id: 'Cat:Treatment Center:Biz:2168',
 *       orgTypeId: 'Cat:Treatment Center',
 *       firmId: 2168,
 *       name: 'Ascension Recovery',
 *     },
 *     ...
 *   ],
 *   contacts: [
 *     {
 *       businessId: 'Cat:Treatment Center:Biz:2168',
 *       name: 'Aaron Kovac',
 *       personId: 3566,
 *     },
 *     {
 *       businessId: null, // for unaffiliated contacts
 *       name: 'Aaron Kovac',
 *       personId: 3566,
 *     },
 *     ...
 *   ],
 * }
 */

const styles = (theme) => ({
  sourceTypeAutocomplete: {
    display: 'inline-block',
    // marginRight: '34px',
  },
  autocompleteAndPlusDiv: {
    display: 'inherit',
  },
  containedAutocompleteDiv: {
    display: 'inline-block',
    width: '100%',
  },
  visiblePlus: {
    display: 'inherit',
    cursor: 'pointer',
    visibility: 'visible',
    paddingLeft: '4px',
    transform: 'translate(0, 14px) scale(0.8)',
  },
  hiddenPlus: {
    display: 'none',
  },
});

class SearchableContactHierarchy extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      chosenContactOrgType: null,
      chosenContactBusiness: null,
      chosenContactContact: null,
      shouldShowBusinessDropdown: true,
      shouldShowContactDropdown: true,
    };
  }

  componentDidMount() {
    this.setChosenOptionsFromProps();
  }

  componentDidUpdate(oldProps) {
    const newProps = this.props;
    if (oldProps.contactId !== newProps.contactId) {
      this.setChosenContactOptionForContactId(newProps.contactId);
    } else if (newProps.companyId !== oldProps.companyId) {
      this.setChosenBusinessOptionForCompanyId(newProps.companyId);
    }
  }

  setChosenOptionsFromProps() {
    const { companyId, contactId } = this.props;
    if (contactId) {
      this.setChosenContactOptionForContactId(contactId);
    } else if (companyId) {
      this.setChosenBusinessOptionForCompanyId(companyId);
    }
  }

  /*
   * In the following function, the "Row N" comments are references to the following table,
   * where "X" means an element is selected, and "O" means it isn't.
   *
   * We assign a string value to selectionState in order to make the code more understandable.
   *
   * Row   OrgType  Business  Contact
   *  1       O        O         O
   *  2       O        O         X
   *  3       O        X         O
   *  4       O        X         X
   *  5       X        O         O
   *  6       X        O         X
   *  7       X        X         O
   *  8       X        X         X
   */

  computeContactChooserOptions() {
    const { chosenContactOrgType, chosenContactBusiness, chosenContactContact } = this.state;

    const selectionState = chosenContactOrgType
      ? chosenContactBusiness
        ? chosenContactContact
          ? /* Row 8*/ 'All Chosen'
          : /* Row 7*/ 'OrgType and Business'
        : chosenContactContact
        ? /* Row 6*/ 'OrgType and Contact'
        : /* Row 5*/ 'Just OrgType'
      : chosenContactBusiness
      ? chosenContactContact
        ? /* Row 4*/ 'Business and Contact'
        : /* Row 3*/ 'Just Business'
      : chosenContactContact
      ? /* Row 2*/ 'Just Contact'
      : /* Row 1*/ 'None Chosen';

    // console.log(`selectionState: ${selectionState}`);

    const data = this.props.data;

    const contactOrgTypeOptions = data.orgTypes;
    let contactBusinessOptions = data.businesses;
    let contactOptions = data.contacts;

    switch (selectionState) {
      case 'None Chosen':
        // already have everything showing, which is what we want
        break;
      case 'Just Contact':
        // already have everything showing, which is what we want
        break;
      case 'Just Business':
        contactOptions = this.getContactOptionsForBusinessOption(chosenContactBusiness);
        break;
      case 'Business and Contact':
        contactOptions = this.getContactOptionsForBusinessOption(chosenContactBusiness);
        break;
      case 'Just OrgType':
        contactBusinessOptions = this.getBusinessOptionsForOrgTypeOption(chosenContactOrgType);
        contactOptions = this.getContactOptionsForBusinessOptions(contactBusinessOptions);
        break;
      case 'OrgType and Contact':
        contactBusinessOptions = this.getBusinessOptionsForOrgTypeOption(chosenContactOrgType);
        contactOptions = this.getContactOptionsForBusinessOptions(contactBusinessOptions);
        break;
      case 'OrgType and Business':
        contactBusinessOptions = this.getBusinessOptionsForOrgTypeOption(chosenContactOrgType);
        contactOptions = this.getContactOptionsForBusinessOption(chosenContactBusiness);
        break;
      case 'All Chosen':
        contactBusinessOptions = this.getBusinessOptionsForOrgTypeOption(chosenContactOrgType);
        contactOptions = this.getContactOptionsForBusinessOption(chosenContactBusiness);
        break;
      default:
        break;
    }

    return { contactOrgTypeOptions, contactBusinessOptions, contactOptions };
  }

  getBusinessOptionsForOrgTypeOption(orgTypeOption) {
    return this.props.data.businesses.filter((bizObj) => bizObj.orgTypeId === orgTypeOption.id);
  }

  getContactOptionsForBusinessOption(businessOption) {
    return this.props.data.contacts.filter((contactObj) => contactObj.businessId === businessOption.id);
  }

  getContactOptionsForBusinessOptions(businessOptions) {
    let contactOptions = [];
    businessOptions.forEach((businessOption) => {
      const contactOptionsForThisBusiness = this.getContactOptionsForBusinessOption(businessOption);
      contactOptions = [...contactOptions, ...contactOptionsForThisBusiness];
    });
    return contactOptions;
  }

  setChosenContactOrgType(chosenOrgTypeOption) {
    if (chosenOrgTypeOption) {
      console.log(`setting chosenContactOrgType: *${chosenOrgTypeOption.name}*`);
      console.log('clearing chosenContactBusiness and chosenContactContact');
      this.setState({
        chosenContactOrgType: chosenOrgTypeOption,
        chosenContactBusiness: null,
        chosenContactContact: null,
      });
      this.props.onOrgTypeSelect(chosenOrgTypeOption.name);
    } else {
      console.log(`clearing chosenContactOrgType, chosenContactBusiness, and chosenContactContact`);
      this.setState({
        chosenContactOrgType: null,
        chosenContactBusiness: null,
        chosenContactContact: null,
      });
    }
    this.setDropdownsVisibilityForOrgTypeOption(chosenOrgTypeOption);
  }

  setDropdownsVisibilityForOrgTypeOption(orgTypeOption) {
    if (orgTypeOption && orgTypeOption.special) {
      const nKids = orgTypeOption.nKids;
      this.setState({ shouldShowContactDropdown: nKids >= 2 || this.state.chosenContactContact });
      this.setState({ shouldShowBusinessDropdown: nKids >= 1 });
    } else {
      this.setState({ shouldShowContactDropdown: true, shouldShowBusinessDropdown: true });
    }
  }

  setChosenBusinessOptionFromUi(chosenBusinessOption) {
    this.setChosenBusinessOption(chosenBusinessOption);
  }

  setChosenBusinessOption(chosenBusinessOption) {
    if (chosenBusinessOption) {
      const orgTypeOption = this.getOrgTypeOptionForBusinessOption(chosenBusinessOption);
      console.log(`setting chosenContactOrgType: *${orgTypeOption.name}*`);
      console.log(`setting chosenContactBusiness: *${chosenBusinessOption.name}*`);
      console.log("clearing chosenContactContact unless it's  unique");

      let chosenContactOption = null;
      let calledOnContactSelectForSingleContact = false;
      if (!orgTypeOption.nKids || orgTypeOption.nKids >= 2) {
        const contactOptions = this.getContactOptionsForBusinessOption(chosenBusinessOption);
        if (contactOptions.length === 1) {
          chosenContactOption = contactOptions[0];
          const referrerType = orgTypeOption.name;
          const referringOrgId = chosenBusinessOption.firmId;
          this.props.onContactSelect(referrerType, referringOrgId, chosenContactOption.personId);
          calledOnContactSelectForSingleContact = true;
        }
      }

      this.setState({
        chosenContactOrgType: orgTypeOption,
        chosenContactBusiness: chosenBusinessOption,
        chosenContactContact: chosenContactOption,
      });

      this.setDropdownsVisibilityForOrgTypeOption(orgTypeOption);
      if (!chosenBusinessOption.terminal && !calledOnContactSelectForSingleContact) {
        const referrerOrgId = chosenBusinessOption.firmId || 0;
        this.props.onCompanySelect(orgTypeOption.name, referrerOrgId);
      }
    } else {
      console.log(`clearing chosenContactBusiness, and chosenContactContact`);
      this.setState({ chosenContactBusiness: null, chosenContactContact: null });
    }
  }

  getOrgTypeOptionForBusinessOption(businessOption) {
    const orgTypeOptions = this.props.data.orgTypes;
    return orgTypeOptions.find((orgTypeObj) => orgTypeObj.id === businessOption.orgTypeId);
  }

  setChosenBusinessOptionForCompanyId(companyId) {
    const businessOption = this.getBusinessOptionForCompanyId(companyId);
    this.setChosenBusinessOption(businessOption);
  }

  getBusinessOptionForCompanyId(companyId) {
    const businessOptions = this.props.data.businesses;
    return businessOptions.find((option) => option.firmId === companyId);
  }

  setChosenContactOptionForContactId(contactId) {
    const contactOption = this.getContactOptionForContactId(contactId);
    this.setChosenContactOption(contactOption);
  }

  getContactOptionForContactId(contactId) {
    const contactOptions = this.props.data.contacts;
    return contactOptions.find((option) => option.personId === contactId);
  }

  setChosenContactOptionFromUi(contactOption) {
    this.setChosenContactOption(contactOption);
  }

  setChosenContactOption(contactOption) {
    if (contactOption) {
      const businessOption = this.getBusinessOptionForContactOption(contactOption);
      if (businessOption) {
        const orgTypeOption = this.getOrgTypeOptionForBusinessOption(businessOption);
        console.log(`setting chosenContactOrgType: *${orgTypeOption.name}*`);
        console.log(`setting chosenContactBusiness: *${businessOption.name}*`);
        console.log(`setting chosenContactContact: *${contactOption.name}*`);
        this.setState(
          {
            chosenContactOrgType: orgTypeOption,
            chosenContactBusiness: businessOption,
            chosenContactContact: contactOption,
          },
          () => this.setDropdownsVisibilityForOrgTypeOption(orgTypeOption)
        );
        const referrerOrgId = businessOption.firmId || 0;
        this.props.onContactSelect(orgTypeOption.name, referrerOrgId, contactOption.personId);
      } else {
        // contactOption is for an unaffiliated contact
        this.setState({
          chosenContactOrgType: null,
          chosenContactBusiness: null,
          chosenContactContact: contactOption,
        });
        this.props.onContactSelect(null, 0, contactOption.personId);
      }
    } else {
      console.log(`clearing chosenContactContact`);
      this.setState({ chosenContactContact: null });
    }
  }

  getBusinessOptionForContactOption(contactOption) {
    const businessOptions = this.props.data.businesses;
    return businessOptions.find((bizObj) => bizObj.id === contactOption.businessId);
  }

  chosenBusinessIsEmptyOrUnique(chosenContactBusiness, contactBusinessOptions) {
    return !chosenContactBusiness || contactBusinessOptions.length <= 1;
  }

  chosenContactIsEmptyOrUnique(chosenContactContact, contactOptions) {
    return !chosenContactContact || contactOptions.length <= 1;
  }

  orgTypeAllowsMultipleContacts(orgTypeOption) {
    // TODO I think this is a terrible hack
    // The SearchableContactHierarchy shouldn't know about the semantics of the orgTypes
    const optionName = orgTypeOption.name;
    return optionName !== 'Private Practitioner';
  }

  handleClickOnNewCompanyPlusIcon(chosenContactOrgType, chosenContactBusiness) {
    const orgTypeName = chosenContactOrgType.name;
    if (!chosenContactOrgType.special) {
      this.props.newCompanyFunc(orgTypeName);
    }
  }

  handleClickOnNewContactPlusIcon(chosenContactOrgType, chosenContactBusiness) {
    if (chosenContactBusiness) {
      if (!chosenContactBusiness.terminal) {
        const chosenOrgTypeName = chosenContactOrgType ? chosenContactOrgType.name : '';
        const chosenFirmId = chosenContactBusiness ? chosenContactBusiness.firmId : 0;
        this.props.newReferralContactFunc(chosenOrgTypeName, chosenFirmId);
      }
    } else {
      this.props.newReferralContactFunc(null, 0);
    }
  }

  computeCompanyInputLabelForOrgType(orgType) {
    if (!orgType) {
      return 'Referral Company';
    }
    if (orgType.kidLabel) {
      return orgType.kidLabel;
    }
    if (orgType.name.endsWith('Consultant')) {
      return 'Consultancy';
    }
    if (orgType.name.endsWith('Practitioner')) {
      return 'Pratice';
    }
    return 'Referral Company';
  }

  computeContactInputLabelForOrgType(orgType) {
    if (!orgType) {
      return 'Referral Contact';
    }
    if (orgType.name.endsWith('Consultant')) {
      return 'Consultant';
    }
    if (orgType.name.endsWith('Practitioner')) {
      return 'Pratitioner';
    }
    return 'Referral Contact';
  }

  render() {
    const { classes } = this.props;

    const { contactOrgTypeOptions, contactBusinessOptions, contactOptions } = this.computeContactChooserOptions();
    let { chosenContactOrgType, chosenContactBusiness, chosenContactContact } = this.state;
    if (contactBusinessOptions.length === 1) {
      chosenContactBusiness = contactBusinessOptions[0];
    }
    if (
      contactOptions.length === 1 &&
      chosenContactOrgType &&
      (!chosenContactOrgType.nKids || chosenContactOrgType.nKids >= 2)
    ) {
      chosenContactContact = contactOptions[0];
    }
    let companyInputLabel = this.computeCompanyInputLabelForOrgType(chosenContactOrgType);
    let contactInputLabel = this.computeContactInputLabelForOrgType(chosenContactOrgType);

    const companyPlusClass =
      chosenContactOrgType && this.chosenBusinessIsEmptyOrUnique(chosenContactBusiness, contactBusinessOptions)
        ? classes.visiblePlus
        : classes.hiddenPlus;
    const okTimeToAddOrgContact =
      chosenContactOrgType &&
      chosenContactBusiness &&
      this.orgTypeAllowsMultipleContacts(chosenContactOrgType) &&
      this.chosenContactIsEmptyOrUnique(chosenContactContact, contactOptions);
    const okTimeToDefinedUnaffiliated = !chosenContactOrgType && !chosenContactBusiness;
    const shouldShowContactPlus = okTimeToDefinedUnaffiliated || okTimeToAddOrgContact;
    const contactPlusClass = shouldShowContactPlus ? classes.visiblePlus : classes.hiddenPlus;

    const required = this.props.required;
    const inputLabelProps = required ? { style: { color: allevaOrange } } : {};
    let refOrgTypeLabel = 'Referral Organization Type';
    if (required) {
      refOrgTypeLabel += ' *';
      companyInputLabel += ' *';
      contactInputLabel += ' *';
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} lg={4} className={classes.sourceTypeAutocomplete}>
          <Autocomplete
            blurOnSelect={true}
            options={contactOrgTypeOptions}
            getOptionLabel={(option) => option.name}
            value={chosenContactOrgType}
            renderInput={(params) => (
              <TextField {...params} label={refOrgTypeLabel} InputLabelProps={inputLabelProps} variant="standard" />
            )}
            onChange={(event, value) => this.setChosenContactOrgType(value)}
            onClose={() => console.log('got onClose')}
            onOpen={
              /* a hack to prevent past values cluttering UI*/ (event) => {
                event.target.autocomplete = 'new-password';
              }
            }
          />
        </Grid>
        {this.state.shouldShowBusinessDropdown && (
          <Grid item xs={12} lg={4} className={classes.autocompleteAndPlusDiv}>
            <div className={classes.containedAutocompleteDiv}>
              <Autocomplete
                blurOnSelect={true}
                options={contactBusinessOptions}
                getOptionLabel={(option) => option.name}
                value={chosenContactBusiness}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={companyInputLabel}
                    InputLabelProps={inputLabelProps}
                    variant="standard"
                  />
                )}
                onChange={(event, value) => {
                  this.setChosenBusinessOptionFromUi(value);
                }}
                onClose={() => console.log('got onClose')}
                onOpen={
                  /* a hack to prevent past values cluttering UI*/ (event) => {
                    event.target.autocomplete = 'new-password';
                  }
                }
              />
            </div>
            <div
              className={companyPlusClass}
              onClick={() => this.handleClickOnNewCompanyPlusIcon(chosenContactOrgType, chosenContactBusiness)}
            >
              <PlusIcon />
            </div>
          </Grid>
        )}
        {this.state.shouldShowContactDropdown && (
          <Grid item xs={12} lg={4} className={classes.autocompleteAndPlusDiv}>
            <div className={classes.containedAutocompleteDiv}>
              <Autocomplete
                blurOnSelect={true}
                options={contactOptions}
                getOptionLabel={(option) => option.name}
                value={chosenContactContact}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={contactInputLabel}
                    InputLabelProps={inputLabelProps}
                    variant="standard"
                  />
                )}
                onChange={(event, value) => this.setChosenContactOptionFromUi(value)}
                onClose={() => console.log('got onClose')}
              />
            </div>
            <div
              className={contactPlusClass}
              onClick={() => this.handleClickOnNewContactPlusIcon(chosenContactOrgType, chosenContactBusiness)}
            >
              <PlusIcon />
            </div>
          </Grid>
        )}
      </Grid>
    );
  }
}

export default withStyles(styles)(SearchableContactHierarchy);

class ContentBuilder {
  includeSimpleOrgTypes = false;

  build(relatedContact) {
    return this.createAccountSpecificData(relatedContact);
  }

  createAccountSpecificData(relatedContact) {
    const knownFirms = DA.getAllKnownFirms();

    // First collect all the orgTypes
    const orgSourceTypeDropdownNames = sortedDropdownItemLabels('Referrer Organization Type');
    const menuDeclaredOrgTypesSet = new Set(orgSourceTypeDropdownNames);
    const knownFirmTypes = knownFirms.filter((firm) => firm.firmType).map((firm) => firm.firmType);
    let nonMenuOrgTypesList = [];
    knownFirmTypes.forEach((firmType) => {
      if (!menuDeclaredOrgTypesSet.has(firmType)) {
        nonMenuOrgTypesList = [...nonMenuOrgTypesList, firmType];
        menuDeclaredOrgTypesSet.add(firmType);
      }
    });
    nonMenuOrgTypesList = nonMenuOrgTypesList.sort();
    const allOrgOrgTypes = [...orgSourceTypeDropdownNames, ...nonMenuOrgTypesList];

    const orgTypes = allOrgOrgTypes.map((name) => {
      return { id: 'Cat:' + name, name: name };
    });

    // Now, run through firms adding to both businesses and contacts
    const firmsWithType = knownFirms.filter((firm) => firm.firmType);
    let businesses = [];
    let contacts = [];
    firmsWithType.forEach((firm) => {
      const orgTypeName = firm.firmType;
      const orgTypeObj = orgTypes.find((catObj) => catObj.name === orgTypeName);
      const orgTypeId = orgTypeObj.id;
      const businessId = orgTypeId + ':Biz:' + firm.id;
      const businessObj = {
        id: businessId,
        orgTypeId,
        name: firm.name,
        firmId: firm.id,
      };
      businesses = [...businesses, businessObj];
      const contactsFromRedux = firm.contacts;
      contactsFromRedux.forEach((contact) => {
        const contactObj = {
          businessId,
          name: contact.name,
          personId: contact.id,
        };
        contacts = [...contacts, contactObj];
      });
    });

    if (relatedContact) {
      contacts = [...contacts, relatedContact];
      businesses = this.possiblyAddNewQuasiBusinessForExtraContact(businesses, relatedContact);
    }

    const unaffilContacts = DA.getUnaffiliatedContacts();
    if (unaffilContacts) {
      const unaffilContactObjs = unaffilContacts.map((obj) => {
        return { businessId: null, name: obj.name, personId: obj.id };
      });
      contacts = [...contacts, ...unaffilContactObjs];
    }

    businesses = businesses.sort((b1, b2) => b1.name.localeCompare(b2.name));
    contacts = contacts.sort((c1, c2) => c1.name.localeCompare(c2.name));

    return { orgTypes, businesses, contacts };
  }

  possiblyAddNewQuasiBusinessForExtraContact(businesses, extraContact) {
    const businessId = extraContact.businessId;
    /*
     * extraContact looks like this:
     * {
     *   businessId: 'Cat:Relative:Name:Vater',
     *   relationship: 'Vater',
     *   name: 'Heinrich Heimlich',
     *   personId: 4820,
     * }
     */
    const startOfNameSection = businessId.indexOf(':Name:');
    const orgTypeId = businessId.substring(0, startOfNameSection);
    const quasiBusinessName = businessId.substring(startOfNameSection + 6);
    const extraBusinessObj = {
      id: businessId,
      orgTypeId,
      terminal: true,
      name: quasiBusinessName,
    };
    return [...businesses, extraBusinessObj];
  }
}

export const searchableContactDataBuilder = new ContentBuilder();
